/**
 * Hook for fetching event correlation analysis data
 *
 * Provides a TanStack Query hook for fetching before/after pain comparison
 * data for a specific event. Follows the existing use-events.ts pattern.
 */

import { useQuery } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type { EventCorrelationResponse } from "@floline/api-client/types";
import { API_URL } from "@/env";

/** Query key factory for event correlations */
export const correlationKeys = {
  all: ["correlations"] as const,
  detail: (eventId: string) => [...correlationKeys.all, eventId] as const,
};

/** API client configured for correlation requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Fetch event correlation data from the API
 *
 * @param eventId - Event ID to get correlation for
 * @returns Correlation response with before/after metrics
 */
async function fetchEventCorrelation(
  eventId: string
): Promise<EventCorrelationResponse | null> {
  try {
    const response = await apiClient.getEventCorrelation(eventId);
    return response.data;
  } catch (error: unknown) {
    // 404 means event does not exist
    if (error instanceof ApiClientError && error.status === 404) {
      return null;
    }
    // Re-throw other errors to be handled by React Query
    throw error;
  }
}

/**
 * Hook to fetch event correlation analysis
 *
 * Returns before/after pain comparison data for an event.
 * Includes metrics for 7, 14, and 30-day analysis windows.
 * If eventId is null, the query is disabled.
 *
 * @param eventId - Event ID or null to disable the query
 *
 * @example
 * ```tsx
 * function EventCorrelationView({ eventId }: { eventId: string }) {
 *   const { correlation, isLoading, insufficientData } = useEventCorrelation(eventId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (insufficientData) return <InsufficientDataMessage message={correlation?.message} />;
 *
 *   return <CorrelationChart data={correlation} />;
 * }
 * ```
 */
export function useEventCorrelation(eventId: string | null) {
  const query = useQuery({
    queryKey: correlationKeys.detail(eventId ?? ""),
    queryFn: () => {
      if (!eventId) {
        throw new Error("Event ID is required");
      }
      return fetchEventCorrelation(eventId);
    },
    // Only run query if we have an ID
    enabled: eventId !== null,
    // Correlation data can be cached longer - it only changes when pain data changes
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Don't retry on 404s (expected when event doesn't exist)
    retry: (failureCount: number, error: Error) => {
      if (
        error instanceof ApiClientError &&
        (error.status === 404 || error.status === 401)
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {
    /** Correlation data, or null if not found */
    correlation: query.data ?? null,
    /** Whether the correlation is currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed with an error (excluding 404) */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Whether there is insufficient data for meaningful analysis */
    insufficientData: query.data?.insufficientData ?? true,
    /** Refetch the correlation manually */
    refetch: query.refetch,
  };
}
